// FamilyPicker — Jul 2026 (Randy). "Who's using this?" family profile tiles.
// Requires a parent (owner/collaborator) signed in on the device; tapping a
// kid tile (+ PIN when set) starts a kid session on this device.
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function FamilyPicker() {
  const { siloId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(null); // kid awaiting PIN
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/family/${siloId}/kids`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [siloId]);

  const start = async (kid, pinValue) => {
    setBusy(true);
    try {
      const r = await api.post(`/family/${siloId}/kids/${kid.id}/session`, { pin: pinValue || null });
      localStorage.setItem("mysilo_kid_token", r.data.token);
      nav("/kid-home");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start — try again.");
      setPin("");
    } finally { setBusy(false); }
  };

  const tap = (kid) => {
    if (kid.revoked) { toast.error("This profile is turned off."); return; }
    if (kid.has_pin) { setPicked(kid); setPin(""); }
    else start(kid, null);
  };

  if (data === null) return <div className="min-h-[100dvh] bg-[#FDFCF8] flex items-center justify-center text-sm text-[#6B6A68]">Loading…</div>;
  if (data === false) return (
    <div className="min-h-[100dvh] bg-[#FDFCF8] flex items-center justify-center p-6 text-center">
      <div>
        <div className="text-4xl mb-3">🔒</div>
        <p className="text-sm text-[#6B6A68]">A parent needs to be signed in on this device to open the family picker.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-[#FDFCF8] flex flex-col items-center justify-center p-6" data-testid="family-picker">
      <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1C1B1A] mb-1">Who's using this?</h1>
      <p className="text-sm text-[#6B6A68] mb-8">{data.silo?.name}</p>
      {data.kids.length === 0 ? (
        <p className="text-sm text-[#6B6A68] italic max-w-xs text-center">No kid profiles yet — a parent can add them from Staging → AI Calendar → Household.</p>
      ) : (
        <div className="flex flex-wrap justify-center gap-5 max-w-2xl">
          {data.kids.map((kid) => (
            <button key={kid.id} onClick={() => tap(kid)} data-testid={`family-kid-tile-${kid.id}`}
              className={`flex flex-col items-center gap-2 group ${kid.revoked ? "opacity-40" : ""}`}>
              <span className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white border-2 border-[#E4E2DC] group-hover:border-[#4A5D4E] flex items-center justify-center text-5xl transition-colors shadow-sm">
                {kid.emoji}
              </span>
              <span className="text-sm text-[#1C1B1A] font-medium">{kid.name}</span>
              {kid.has_pin && <span className="text-[9px] uppercase tracking-[0.18em] text-[#6B6A68]">PIN</span>}
            </button>
          ))}
        </div>
      )}
      <button onClick={() => nav(-1)} className="mt-10 text-xs underline text-[#6B6A68] hover:text-[#1C1B1A]" data-testid="family-picker-back">
        ← I'm a parent, take me back
      </button>

      {picked && (
        <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPicked(null)}>
          <div className="modal-safe bg-[#FDFCF8] rounded-2xl p-6 w-full max-w-xs text-center shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="family-pin-modal">
            <div className="text-4xl mb-2">{picked.emoji}</div>
            <div className="font-serif-display text-xl text-[#1C1B1A] mb-4">Hi {picked.name}! Enter your PIN</div>
            <input
              type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoFocus
              value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter" && pin.length >= 4) start(picked, pin); }}
              data-testid="family-pin-input"
              className="w-40 mx-auto text-center text-2xl tracking-[0.5em] px-3 py-2 border-2 border-[#E4E2DC] rounded-xl bg-white focus:outline-none focus:border-[#4A5D4E]" />
            <div className="flex gap-2 mt-5 justify-center">
              <button onClick={() => setPicked(null)} className="text-xs px-4 py-2 rounded-lg text-[#6B6A68]">Cancel</button>
              <button onClick={() => start(picked, pin)} disabled={busy || pin.length < 4} data-testid="family-pin-go"
                className="text-xs px-5 py-2 rounded-lg bg-[#4A5D4E] text-white disabled:opacity-50">
                {busy ? "…" : "Let's go!"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
